import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Encapsulates directory listing state and mutations.
 * Consumers get the filter setters, the collections and a small set of
 * high-level actions (load / sync / toggleStatus / bulkUpdateStatus).
 * Selection state is intentionally NOT here — it belongs to the page
 * because the bulk-actions bar orchestrates it with the table.
 */
export function useUsers() {
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [q, setQ] = useState("");
  const [department, setDepartment] = useState("__all__");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q, limit: 200 };
      if (department && department !== "__all__") params.department = department;
      if (statusFilter && statusFilter !== "__all__") params.status = statusFilter;
      const [ures, tres, dres] = await Promise.all([
        api.get("/users", { params }),
        api.get("/templates"),
        api.get("/users/departments"),
      ]);
      setUsers(ures.data);
      setTemplates(tres.data);
      setDepartments(dres.data);
    } finally {
      setLoading(false);
    }
  }, [q, department, statusFilter]);

  // Debounced load whenever a filter changes.
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const toggleStatus = useCallback(async (user) => {
    const newStatus = user.status === "disabled" ? "active" : "disabled";
    setTogglingStatus(user.id);
    try {
      await api.put(`/users/${user.id}/status`, { status: newStatus });
      toast.success(
        newStatus === "disabled"
          ? `${user.display_name} desactivado (excluido de futuras publicaciones)`
          : `${user.display_name} activado`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cambiar estado");
    } finally {
      setTogglingStatus(null);
    }
  }, [load]);

  const bulkUpdateStatus = useCallback(async (selectedIds, newStatus) => {
    if (selectedIds.size === 0) return { modified: 0 };
    setBulkProcessing(true);
    try {
      const { data } = await api.post("/users/bulk/status", {
        user_ids: Array.from(selectedIds),
        status: newStatus,
      });
      toast.success(
        newStatus === "disabled"
          ? `${data.modified} usuario(s) desactivado(s). Excluidos de futuras publicaciones.`
          : `${data.modified} usuario(s) activado(s).`
      );
      await load();
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en operación masiva");
      return { modified: 0 };
    } finally {
      setBulkProcessing(false);
    }
  }, [load]);

  const doSync = useCallback(async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/users/sync");
      toast.success(
        data.mode === "graph"
          ? `Sincronización exitosa: ${data.total_synced} usuarios`
          : `Modo demo: ${data.total_synced} usuarios. Configure Graph para sincronizar del tenant.`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error sincronizando");
    } finally {
      setSyncing(false);
    }
  }, [load]);

  // Optimistic mutations for the detail sheet — updates cached lists in place
  // so the sheet closes with fresh data without an extra round-trip.
  const updateLocalUser = useCallback((userId, patch) => {
    setUsers((list) => list.map((u) => (u.id === userId ? { ...u, ...patch } : u)));
  }, []);

  const templateNameOf = useCallback(
    (id) => templates.find((t) => t.id === id)?.name,
    [templates]
  );

  return {
    // data
    users, templates, departments,
    // filters
    q, setQ,
    department, setDepartment,
    statusFilter, setStatusFilter,
    // status flags
    loading, syncing, togglingStatus, bulkProcessing,
    // actions
    load, doSync, toggleStatus, bulkUpdateStatus, updateLocalUser,
    // derived
    templateNameOf,
  };
}
